import { useCallback, useEffect, useMemo, useRef } from 'react';
import { getView, getBoreholeOffsetLines } from '../../../services/viewsSlice';
import { AppDispatch } from '../../../app/store';

interface UseViewLogViewerParams {
  viewIdParam?: string | null;
  clientId?: string;
  projectId?: string;
  boreholeId?: string;
  activeTemplateId?: string | null;
  viewData?: any;
  selectedViewId?: string | null;
  dispatch: AppDispatch;
  history: { push: (path: string) => void };
  setSelectedViewId: (value: string | null) => void;
  setSelectedBoreholeIds: (value: number[]) => void;
  setAddedBoreholeIds: (value: number[]) => void;
  setPendingBoreholeIds: (value: number[]) => void;
  setHasConfirmedBoreholes: (value: boolean) => void;
  viewLoading?: boolean;
  onViewDataReady?: (viewData: any, viewBoreholeIds: number[]) => void;
}

const useViewLogViewer = ({
  viewIdParam,
  clientId,
  projectId,
  boreholeId,
  activeTemplateId,
  viewData,
  selectedViewId,
  dispatch,
  history,
  setSelectedViewId,
  setSelectedBoreholeIds,
  setAddedBoreholeIds,
  setPendingBoreholeIds,
  setHasConfirmedBoreholes,
  viewLoading,
  onViewDataReady
}: UseViewLogViewerParams) => {
  // Track which viewId we've already dispatched getBoreholeOffsetLines for
  const offsetLinesFetchedForViewRef = useRef<string | null>(null);
  // True while we are waiting for the getView response for the current viewIdParam.
  // Prevents the viewData effect from calling onViewDataReady with stale data from a
  // previous view when selectedViewId changes before the new getView response arrives.
  const pendingViewFetchRef = useRef<boolean>(false);
  // Snapshot of viewData at the moment getView was dispatched.  When viewData still
  // matches this snapshot it means the response hasn't arrived yet (it's stale).
  const staleViewDataRef = useRef<any>(null);

  useEffect(() => {
    if (!viewIdParam) return;
    // Mark that we're waiting for fresh data and record the current (stale) viewData
    // so the viewData effect can detect when new data actually arrives.
    console.log('[useViewLogViewer] viewIdParam changed → dispatching getView', viewIdParam, 'stale viewData captured:', viewData?.id ?? viewData?.groups?.length);
    pendingViewFetchRef.current = true;
    staleViewDataRef.current = viewData;
    setSelectedViewId(viewIdParam);
    offsetLinesFetchedForViewRef.current = null; // reset so we re-fetch for the new view
    dispatch(getView(viewIdParam));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewIdParam, dispatch, setSelectedViewId]); // intentionally omits viewData

  const viewBoreholeIds = useMemo(() => {
    if (!selectedViewId || !viewData?.groups) return [] as number[];
    return viewData.groups
      .map((g: any) => Number(g?.boreholeId))
      .filter((id: number) => !isNaN(id));
  }, [selectedViewId, viewData]);

  useEffect(() => {
    if (!selectedViewId || !viewData?.groups) return;
    if (viewBoreholeIds.length === 0) return;
    // Only call onViewDataReady when we are actively waiting for a getView response
    // AND the viewData object has actually changed from the stale snapshot taken when
    // getView was dispatched.  This prevents two fetches when:
    //   (a) selectedViewId updates before the URL param (un-batched renders), or
    //   (b) both change in the same render but viewData is still the previous view's data.
    console.log('[useViewLogViewer] viewData effect fired', {
      selectedViewId,
      pendingFetch: pendingViewFetchRef.current,
      isStale: viewData === staleViewDataRef.current,
      viewBoreholeIds,
      viewDataGroups: viewData?.groups?.length
    });
    if (!pendingViewFetchRef.current) { console.log('[useViewLogViewer] SKIP: no pending fetch'); return; }
    if (viewData === staleViewDataRef.current) { console.log('[useViewLogViewer] SKIP: viewData is still stale'); return; }
    if (viewLoading) { console.log('[useViewLogViewer] SKIP: getView still in flight (viewLoading=true)'); return; }
    pendingViewFetchRef.current = false; // consume the pending flag
    setSelectedBoreholeIds(viewBoreholeIds);
    setAddedBoreholeIds(viewBoreholeIds);
    setPendingBoreholeIds(viewBoreholeIds);
    setHasConfirmedBoreholes(false);
    onViewDataReady?.(viewData, viewBoreholeIds);

    // Always fetch stratigraphyLexiconStartAndEnds so manual depth mode
    // has the actual absolute surface depths available.
    // Guard with a ref to avoid re-dispatching when the fulfilled action updates viewData.
    if (viewData.topOffsetStratigraphyTrackTypeId && offsetLinesFetchedForViewRef.current !== selectedViewId) {
      offsetLinesFetchedForViewRef.current = selectedViewId ?? null;
      dispatch(getBoreholeOffsetLines({
        stratigraphyTrackTypeId: viewData.topOffsetStratigraphyTrackTypeId,
        boreholeIds: viewBoreholeIds
      }));
    }
  }, [selectedViewId, viewData, viewLoading, viewBoreholeIds, setSelectedBoreholeIds, setAddedBoreholeIds, setPendingBoreholeIds, setHasConfirmedBoreholes, onViewDataReady, dispatch]);

  const onViewSelectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextId = e.target.value || null;
    setSelectedViewId(nextId);
    if (!clientId || !projectId || !boreholeId) return;
    if (nextId) {
      offsetLinesFetchedForViewRef.current = null; // reset for new view
      dispatch(getView(nextId));
      history.push(`/client/${clientId}/project/${projectId}/borehole/${boreholeId}/log-viewer/view/${nextId}`);
      return;
    }
    if (activeTemplateId) {
      history.push(`/client/${clientId}/project/${projectId}/borehole/${boreholeId}/log-viewer/${activeTemplateId}`);
      return;
    }
    history.push(`/client/${clientId}/project/${projectId}/borehole/${boreholeId}/log-viewer`);
  }, [setSelectedViewId, clientId, projectId, boreholeId, dispatch, history, activeTemplateId]);

  return {
    isViewMode: Boolean(selectedViewId),
    viewBoreholeIds,
    onViewSelectChange
  };
};

export default useViewLogViewer;
